import { Component, ElementRef, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { BaseWidgetComponent } from '../base-widget/base-widget.component';
import { VaultService } from 'src/app/services/vault.service';
import { PredictAnalysisService } from 'src/app/services/predict-analysis.service';
import { WidgetService } from 'src/app/services/widget.service';
import { ConfigService } from 'src/app/services/config.service';
import { HttpClient } from '@angular/common/http';
import { catchError, of } from 'rxjs';
import { TeamsService } from 'src/app/services/teams.service';
import { MatTableDataSource } from '@angular/material/table';
import { MatPaginator } from '@angular/material/paginator';

@Component({
    selector: 'app-playground-widget',
    templateUrl: './playground-widget.component.html',
    styleUrls: ['../base-widget/base-widget.component.scss', './playground-widget.component.scss'],
    standalone: false
})
export class PlaygroundWidgetComponent extends BaseWidgetComponent implements OnInit {
  @Input() vaultName: string = null;
  @Output() onOpenDetail = new EventEmitter<{ vaultName: string; sampleIndex: number; sampleData: any }>();

  public title: string = 'Playground';
  public allModels: any[] = [];
  public allModelsLoading = false;
  public isLoading = false;

  // Playground state
  public invokeError: string = null;
  public modelPerf: any = null;
  public browseLoading = false;
  public browseTableLoading = false;
  public browseLoaded = false;
  public browseLoadingMessage = 'Loading test predictions...';
  public browseData: any = null;
  public browseTableDS = new MatTableDataSource<any>([]);
  public browseSort = 'index';
  public browseFilter = 'all';
  public browseOffset = 0;
  public browsePageSize = 50;
  public browseFilteredCount = 0;
  public importPredicting = false;
  public importPredictProgress = 0;
  public playgroundSource: 'blank' | 'test' | 'import' = 'blank';
  public exportPath: string = null;
  public exportLoading = false;
  @ViewChild('browsePaginator') browsePaginator: MatPaginator;


  constructor(
    private el: ElementRef,
    private vaultService: VaultService,
    private predictAnalysis: PredictAnalysisService,
    private widgetService: WidgetService,
    private http: HttpClient,
    private teamsService: TeamsService
  ) {
    super();
  }

  ngOnInit(): void {
    this.loadAllModels();

    if (this.vaultName) {
      this.loadPersistedSource();
      this.loadModelPerf();
      setTimeout(() => {
        this.optimizePageSize(false);
        if (this.playgroundSource === 'test') {
          this.loadBrowse();
        } else if (this.playgroundSource === 'import') {
          this.loadCachedImport();
        }
      });
    }

    this._subs.add = this.widgetService.resize$.subscribe(() => {
      this.optimizePageSize();
    });

    this._subs.add = this.widgetService.playgroundSampleUpdate$.subscribe((update) => {
      if (update.teamName !== this.teamName || update.modelName !== this.vaultName) return;
      const data = this.browseTableDS.data;
      const row = data.find(r => r.index === update.sampleIndex);
      if (row) {
        row.prediction = update.prediction;
        row.ground_truth = update.ground_truth;
        row.error = update.error;
        row.correct = update.correct;
        this.browseTableDS.data = [...data];
      }
    });
  }

  private loadAllModels(): void {
    this.allModelsLoading = true;
    const sub = this.vaultService.load(this.teamName).subscribe((response: any[]) => {
      sub.unsubscribe();
      this.allModels = response || [];
      this.allModelsLoading = false;
      if (!this.vaultName) {
        this.loadPersistedModel();
      }
    });
  }

  onModelSelect(modelName: string): void {
    this.vaultName = modelName;
    this.persistModel();
    const model = this.allModels.find(m => m.name === modelName);
    // Reset browse state
    this.browseLoaded = false;
    this.browseData = null;
    this.browseTableDS.data = [];
    this.browseOffset = 0;
    this.browseFilteredCount = 0;
    this.modelPerf = null;
    this.invokeError = null;
    // Load persisted source and model perf for this model
    this.loadPersistedSource();
    this.loadModelPerf();
    if (this.playgroundSource === 'test') {
      this.optimizePageSize(false);
      this.loadBrowse();
    } else if (this.playgroundSource === 'import') {
      this.optimizePageSize(false);
      this.loadCachedImport();
    }
  }

  // Load model perf metrics
  private loadModelPerf(): void {
    if (!this.vaultName) return;
    const sub = this.vaultService.get(this.teamName, this.vaultName).pipe(
      catchError(() => of(null))
    ).subscribe((response: any) => {
      sub.unsubscribe();
      this.modelPerf = response?.vaultItem?.perf || null;
    });
  }

  // Model persistence
  private get modelStorageKey(): string {
    return `playground-model-${this.teamName}`;
  }

  private loadPersistedModel(): void {
    const saved = localStorage.getItem(this.modelStorageKey);
    if (saved && this.allModels.some(m => m.name === saved)) {
      this.onModelSelect(saved);
    }
  }

  private persistModel(): void {
    if (this.vaultName) {
      localStorage.setItem(this.modelStorageKey, this.vaultName);
    }
  }

  // Source persistence
  private get sourceStorageKey(): string {
    return `playground-source-${this.teamName}-${this.vaultName}`;
  }

  private loadPersistedSource(): void {
    const saved = localStorage.getItem(this.sourceStorageKey);
    if (saved === 'test' || saved === 'import') {
      this.playgroundSource = saved;
    } else {
      this.playgroundSource = 'blank';
    }
  }

  private persistSource(): void {
    localStorage.setItem(this.sourceStorageKey, this.playgroundSource);
  }

  onSourceChange(value: 'blank' | 'test' | 'import'): void {
    if (value === 'import') {
      return;
    }
    this.playgroundSource = value;
    this.persistSource();
    this.browseData = null;
    this.browseTableDS.data = [];
    this.browseFilteredCount = 0;
    this.browseLoaded = false;
    this.browseOffset = 0;
    this.invokeError = null;
    if (value === 'test') {
      this.loadBrowse();
    }
  }

  onImportClick(): void {
    this.triggerImport();
  }

  private triggerImport(): void {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.h5,.hdf5';
    input.onchange = (event: any) => {
      const file = event.target.files?.[0];
      if (!file) return;
      this.uploadImportFile(file);
    };
    input.click();
  }

  private async loadCachedImport(): Promise<void> {
    const teamConfig = await this.teamsService.getConfigAsync(this.teamName);
    const cachePath = `${teamConfig.path.vault}/${this.vaultName}/playground_import.h5`;
    this.browseLoading = true;
    this.invokeError = null;

    try {
      const [parseResult, modelInfo] = await Promise.all([
        this.predictAnalysis.parseImport(this.teamName, this.vaultName, cachePath).toPromise(),
        this.predictAnalysis.getModelInfo(this.teamName, this.vaultName).toPromise()
      ]);

      if (parseResult.error) {
        this.playgroundSource = 'blank';
        this.persistSource();
        this.browseLoading = false;
        return;
      }

      const labelFeature = modelInfo?.labels?.[0]?.feature || null;
      const samples = parseResult.samples.map((s: any) => {
        let groundTruth = null;
        if (labelFeature && s.features[labelFeature]) {
          const labelData = s.features[labelFeature];
          groundTruth = Array.isArray(labelData.values)
            ? (Array.isArray(labelData.values[0]) ? labelData.values[0][0] : labelData.values[0])
            : labelData.values;
        }
        return {
          index: s.index,
          prediction: null,
          ground_truth: groundTruth,
          error: null,
          correct: null,
          _features: s.features
        };
      });

      this.browseData = null;
      this.browseTableDS.data = samples;
      this.browseFilteredCount = samples.length;
      this.browseLoaded = true;
      this.browseLoading = false;

      setTimeout(() => {
        if (this.browsePaginator) {
          this.browseTableDS.paginator = this.browsePaginator;
          this.optimizePageSize();
        }
      });

      this.predictAllImported(samples, cachePath);
    } catch (err) {
      this.playgroundSource = 'blank';
      this.persistSource();
      this.browseLoading = false;
    }
  }

  private async uploadImportFile(file: File): Promise<void> {
    this.browseLoading = true;
    this.invokeError = null;

    const teamConfig = await this.teamsService.getConfigAsync(this.teamName);
    const cachePath = `${teamConfig.path.vault}/${this.vaultName}/playground_import.h5`;

    const reader = new FileReader();
    reader.onload = async () => {
      try {
        const arrayBuffer = reader.result as ArrayBuffer;

        await this.postBinaryFile(cachePath, arrayBuffer);

        const [parseResult, modelInfo] = await Promise.all([
          this.predictAnalysis.parseImport(this.teamName, this.vaultName, cachePath).toPromise(),
          this.predictAnalysis.getModelInfo(this.teamName, this.vaultName).toPromise()
        ]);

        if (parseResult.error) {
          this.invokeError = parseResult.error;
          this.browseLoading = false;
          return;
        }

        const labelFeature = modelInfo?.labels?.[0]?.feature || null;
        const samples = parseResult.samples.map((s: any) => {
          let groundTruth = null;
          if (labelFeature && s.features[labelFeature]) {
            const labelData = s.features[labelFeature];
            groundTruth = Array.isArray(labelData.values)
              ? (Array.isArray(labelData.values[0]) ? labelData.values[0][0] : labelData.values[0])
              : labelData.values;
          }
          return {
            index: s.index,
            prediction: null,
            ground_truth: groundTruth,
            error: null,
            correct: null,
            _features: s.features
          };
        });

        this.playgroundSource = 'import';
        this.persistSource();
        localStorage.setItem(`${this.sourceStorageKey}-filename`, file.name);
        this.browseData = null;
        this.browseTableDS.data = samples;
        this.browseFilteredCount = samples.length;
        this.browseLoaded = true;
        this.browseLoading = false;

        setTimeout(() => {
          if (this.browsePaginator) {
            this.browseTableDS.paginator = this.browsePaginator;
            this.optimizePageSize();
          }
        });

        this.predictAllImported(samples, cachePath);
      } catch (err: any) {
        this.invokeError = err?.message || 'Failed to upload import file';
        this.browseLoading = false;
      }
    };
    reader.readAsArrayBuffer(file);
  }

  private postBinaryFile(path: string, data: ArrayBuffer): Promise<void> {
    return new Promise((resolve, reject) => {
      const bytes = new Uint8Array(data);
      let binary = '';
      for (let i = 0; i < bytes.byteLength; i++) {
        binary += String.fromCharCode(bytes[i]);
      }
      const base64 = btoa(binary);

      const sub = this.http.post(`${ConfigService.apiUrl}/file/${this.teamName}`, {
        path,
        type: 'binary',
        data: base64
      }).subscribe({
        error: (err) => { sub.unsubscribe(); reject(err); },
        complete: () => { sub.unsubscribe(); resolve(); }
      });
    });
  }

  private async predictAllImported(samples: any[], cachePath: string): Promise<void> {
    if (samples.length === 0) return;

    this.importPredicting = true;
    this.importPredictProgress = 0;

    this.browseData = {
      total_samples: samples.length,
      accuracy: null
    };

    try {
      const result = await this.predictAnalysis.predictBatch(this.teamName, this.vaultName, cachePath).toPromise();

      if (result.error) {
        console.error('Batch prediction failed:', result.error);
        this.importPredicting = false;
        return;
      }

      for (const r of result.results) {
        const sample = samples[r.index];
        if (sample) {
          sample.prediction = r.prediction;
          sample.ground_truth = r.ground_truth;
          sample.error = r.error;
          sample.correct = r.correct;
        }
      }

      this.browseData.accuracy = result.accuracy;
      this.importPredictProgress = 100;
      this.browseTableDS.data = [...samples];
    } catch (err) {
      console.error('Batch prediction failed:', err);
    }

    this.importPredicting = false;
  }

  onSampleClick(row: any): void {
    console.log('[PlaygroundWidget] onSampleClick', row, 'observers:', this.onOpenDetail.observers?.length);
    this.onOpenDetail.emit({ vaultName: this.vaultName, sampleIndex: row.index, sampleData: row });
  }

  addNewRow(): void {
    const newIndex = this.browseTableDS.data.length;
    const newRow = { index: newIndex, prediction: null, ground_truth: null, error: null, correct: null, _isNew: true };
    this.browseTableDS.data = [...this.browseTableDS.data, newRow];
    this.browseFilteredCount = this.browseTableDS.data.length;
    setTimeout(() => {
      if (this.browsePaginator) {
        this.browseTableDS.paginator = this.browsePaginator;
        this.browsePaginator.lastPage();
      }
    });
  }

  exportPlayground(): void {
    if (this.playgroundSource === 'test') {
      this.exportLoading = true;
      this.exportPath = null;
      this.predictAnalysis.prepareExport(this.teamName, this.vaultName).subscribe({
        next: (result) => {
          this.exportPath = result.path;
          this.exportLoading = false;
        },
        error: (err) => {
          console.error('Export failed:', err);
          this.invokeError = 'Export failed: ' + (err.error?.message || err.message);
          this.exportLoading = false;
        }
      });
    } else {
      if (this.browseTableDS.data.length === 0) return;
      const rows = this.browseTableDS.data;
      const headers = ['index', 'prediction', 'ground_truth', 'error', 'correct'];
      const csvLines = [headers.join(',')];
      for (const row of rows) {
        csvLines.push(headers.map(h => row[h] ?? '').join(','));
      }
      const blob = new Blob([csvLines.join('\n')], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${this.vaultName}_playground.csv`;
      a.click();
      URL.revokeObjectURL(url);
    }
  }

  openExportInFiles(): void {
    if (!this.exportPath) return;
    const lastSlash = this.exportPath.lastIndexOf('/');
    const directory = this.exportPath.substring(0, lastSlash);
    const filename = this.exportPath.substring(lastSlash + 1);

    const widget = this.widgetService.createWidget('FilesWidget', this.teamName, {}, {
      baseInputs: { initialPath: directory, selectFile: filename },
      outputs: {
        onClose: ($event: BaseWidgetComponent.Event) => {
          setTimeout(() => this.widgetService.removeWidget(this.teamName, $event.id));
        },
        onRestore: ($event: BaseWidgetComponent.Event) => {
          this.widgetService.restoreWidget(this.teamName, $event.id);
        },
        onMaximize: ($event: BaseWidgetComponent.Event) => {
          this.widgetService.maximizeWidget(this.teamName, $event.id);
        },
        onToggle: ($event: BaseWidgetComponent.Event) => {
          this.widgetService.toggleWidget(this.teamName, $event.id);
        },
        onDock: ($event: BaseWidgetComponent.DockEvent) => {
          this.widgetService.dockWidget(this.teamName, $event.id, $event.position);
        },
        onUndock: ($event: BaseWidgetComponent.Event) => {
          this.widgetService.undockWidget(this.teamName, $event.id);
        }
      }
    });
    this.widgetService.addWidget(this.teamName, widget);
  }

  private optimizePageSize(triggerReload = true): void {
    const ROW_HEIGHT = 28;
    const OVERHEAD = 200;
    const hostHeight = this.el.nativeElement.offsetHeight;
    if (!hostHeight) return;
    const available = hostHeight - OVERHEAD;
    const newSize = Math.max(5, Math.floor(available / ROW_HEIGHT) - 4);
    if (newSize !== this.browsePageSize) {
      this.browsePageSize = newSize;
      if (triggerReload && this.browsePaginator) {
        setTimeout(() => this.browsePaginator._changePageSize(this.browsePageSize), 100);
      }
    }
  }

  private loadBrowse(): void {
    if (!this.vaultName) return;

    if (!this.browseData) {
      this.browseLoading = true;
      this.browseLoadingMessage = 'Running predictions on test set (first load only)...';
    } else {
      this.browseTableLoading = true;
    }
    this.browseLoaded = true;
    this.invokeError = null;

    const sub = this.predictAnalysis.browse(this.teamName, this.vaultName, {
      offset: this.browseOffset,
      limit: this.browsePageSize,
      sort: this.browseSort,
      filter: this.browseFilter
    }).pipe(
      catchError((err) => {
        this.invokeError = err?.error?.message || err?.message || 'Failed to load test predictions';
        this.browseLoading = false;
        this.browseTableLoading = false;
        return of(null);
      })
    ).subscribe((response: any) => {
      sub.unsubscribe();
      this.browseLoading = false;
      this.browseTableLoading = false;
      if (!response) return;

      this.browseData = response;
      this.browseFilteredCount = response.filtered_samples || 0;
      this.browseTableDS.data = response.samples || [];
      setTimeout(() => this.optimizePageSize(false));
    });
  }

  onBrowseSortChange(sort: string): void {
    this.browseSort = sort;
    this.browseOffset = 0;
    this.loadBrowse();
  }

  onBrowseFilterChange(filter: string): void {
    this.browseFilter = filter;
    this.browseOffset = 0;
    this.loadBrowse();
  }

  onBrowsePageChange(event: any): void {
    this.browsePageSize = event.pageSize;
    this.browseOffset = event.pageIndex * event.pageSize;
    this.loadBrowse();
  }
}
